import { PrivacyPolicy } from "./types";

export const PrivacyPolicyContent: PrivacyPolicy = {
  headers: ["Overview"],
  overview: [
    `Welcome to the BrainHealth Platform ("the Platform"). We are committed to protecting your privacy and maintaining the confidentiality of your data. This Privacy Policy explains how we collect, use, share, and protect your information when you participate in research studies through the Platform. It is intended to supplement the information outlined in your study's `,
    `Informed Consent Form`,
    ", not replace it.",
    "Because the BrainHealth Platform can support multiple research studies, some details might vary depending on the specific study in which you are enrolled. If you connect a health or fitness account, we may collect your name, email, and data you authorize (e.g., steps, sleep, heart rate). This is used only for approved research studies. We do not sell, share, or use this data for ads, AI training, or unrelated purposes."
  ],
  sections: [
    // Section 1: Account Creation and Core Reminders
    {
      title: "1. Account Creation and Core Reminders",
      items: [
        {
          content:
            "• Account Setup: You create an account on the Platform (via web or the BrainHealth app) using your email address. This email is used to send you core reminders related to your study activities.",
          indentLevel: 1
        },
        {
          content:
            "• External Email Service: We use a secure, third-party email management service to send out these core reminders.",
          indentLevel: 1
        },
        {
          content:
            "• Core Reminders: Core reminders are an essential part of study participation and cannot be turned off unless you withdraw from the study.",
          indentLevel: 1
        }
      ]
    },

    // Section 2: External Sites
    {
      title: "2. External Sites",
      introduction:
        "The Platform may link you to external sites or services for optional educational, training or research resources:",
      items: [
        {
          content:
            "• Resources Tab: Contains articles, videos, and other materials curated by BrainHealth staff. Accessing or using these resources is optional.",
          indentLevel: 1
        },
        {
          content:
            "• BrainHQ: If your study includes the BrainHQ research-based cognitive training program, you will receive additional information in your study consent about how your data is collected and used through that program.",
          indentLevel: 1
        },
        {
          content:
            "• Research Opportunities: You may be offered other research opportunities in line with The BrainHealth Platform.",
          indentLevel: 1
        }
      ]
    },

    // Section 3: Optional Notification Preferences
    {
      title: "3. Optional Notification Preferences",
      introduction:
        "Beyond the core reminders, you can opt in to receive additional notifications or share specific types of data.",
      items: [
        {
          content:
            "1. Newsletter: Quarterly updates from the Center for BrainHealth.",
          indentLevel: 1
        },
        {
          content:
            "2. Training & Habit Notifications: Reminders to help you stay on track with training sessions and healthy habit building.",
          indentLevel: 1
        },
        {
          content: "3. Health Tracker:",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ You may choose to connect your wearable fitness data (e.g., Google Fit, Apple HealthKit, Android Health Connect) to the Platform.",
              indentLevel: 2
            },
            {
              content:
                "◦ If you opt in, data we collect may include: step count, VO2 max, cardio fitness events, BMI, body fat percentage, glucose levels, heart rate variability, blood pressure (systolic & diastolic), sleep metrics, etc.",
              indentLevel: 2
            },
            {
              content:
                "◦ We use this information to explore how physical health, fitness, and sleep influence brain health measures, such as the BrainHealth Index.",
              indentLevel: 2
            }
          ]
        },
        {
          content:
            "4. Research Opportunities: You may receive notifications about future research opportunities you might qualify for.",
          indentLevel: 1
        },
        {
          content:
            "Opting into any of these additional features is entirely voluntary.",
          indentLevel: 1
        }
      ]
    },

    // Section 4: Data Retention Policy
    {
      title: "4. Data Retention Policy",
      items: [
        {
          content:
            "• Unique Participant Code: Each participant is assigned a unique code to safeguard personal information and maintain privacy. In research analyses, we use de-identified data, ensuring that research activities remain separate from any personal information (name, date of birth, email).",
          indentLevel: 1
        },
        {
          content:
            "• Access to Personally Identifiable Information (PII): Only a limited number of trained and authorized researchers can access the secure database containing your PII. The database is password-protected and monitored. PII includes information such as name, date of birth, email, phone number, and may include your likeness or voice if audio/video recordings are part of your study activities.",
          indentLevel: 1
        },
        {
          content:
            "• Data Storage: All data is stored securely in our research database. If you are enrolled in a sub-study within the Platform, that data is labeled so that it is only pulled for analysis for that sub study.",
          indentLevel: 1
        },
        {
          content:
            "• Data Protection Measures: We use encryption in transit and at rest. Access is limited to authorized staff and regularly reviewed.",
          indentLevel: 1
        },
        {
          content: "• Study Completion/Withdrawal:",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ When the study ends, or if you withdraw from the study, we remove direct identifiers (e.g., name, email) from your data in that study.",
              indentLevel: 2
            },
            {
              content:
                "◦ We retain de-identified data (e.g., assessment scores, survey results) for research purposes. These records will no longer be traceable back to you.",
              indentLevel: 2
            }
          ]
        }
      ]
    },

    // Section 5: Third-Party Integrations for Data Management
    {
      title: "5. Third-Party Integrations for Data Management",
      introduction:
        "We use secure third-party integrations to store and process certain data:",
      items: [
        {
          content:
            "• Cloud-Based Hosting: Your BrainHealth Index data and other information collected through the Platform are stored on a secure, cloud-based system that supports web applications and digital products approved for clinical research.",
          indentLevel: 1
        },
        {
          content:
            "• Web Analytics: We analyze Platform engagement (e.g., page views, click events) using a secure web analytics service. This helps us improve user experience. Your activity within the Platform may be tracked securely in de-identified or aggregated form.",
          indentLevel: 1
        },
        {
          content:
            "• We do not share connected account data with third parties. It is used only within the Platform for research purposes described in your consent.",
          indentLevel: 1
        }
      ]
    },

    // Section 6: Third-Party Integrations: Opt-In Services
    {
      title: "6. Third-Party Integrations: Opt-In Services",
      introduction:
        "If you choose to opt into specific features, the following third parties will have limited access to certain information:",
      items: [
        {
          content: "1. Reminders and Scheduling",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ Text Reminders: If you opt in to receive text reminders, a secure communication platform will store and use your mobile phone number solely for this purpose.",
              indentLevel: 2
            },
            {
              content:
                "◦ Scheduling Coaching Calls: If you schedule a coaching call, a scheduling service will use your email address and name to coordinate the appointment.",
              indentLevel: 2
            }
          ]
        },
        {
          content: "2. Marketing Emails",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ If you opt in to receive marketing or promotional emails, your name and email address will be stored in a platform that manages marketing campaigns, customer service, and business operations.",
              indentLevel: 2
            }
          ]
        },
        {
          content: "3. Audio and Video Recording for Coaching Sessions",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ If you opt in to have a coaching session recorded, audio/video will be saved securely through our videoconference provider. These optional services are never activated without your permission, and any data collected is used only as described in the relevant study's consent form and in accordance with this Privacy Policy.",
              indentLevel: 2
            }
          ]
        }
      ]
    },

    // Section 7: Your Privacy Matters
    {
      title: "7. Your Privacy Matters",
      items: [
        {
          content:
            "• Primary Purpose: We collect and analyze data to advance research on brain health and to provide supportive tools for participants.",
          indentLevel: 1
        },
        {
          content:
            "• Data Protection: Your privacy is paramount. The data we maintain is safeguarded using industry-standard research security measures.",
          indentLevel: 1
        },
        {
          content: "• Your Rights:",
          indentLevel: 1,
          subitems: [
            {
              content:
                "◦ You may withdraw from the BrainHealth Project at any time by visiting your profile settings and selecting 'Opt Out of Project.'",
              indentLevel: 2
            },
            {
              content:
                "◦ You may also contact us at support @thebrainhealthproject.org if you have questions, concerns, or requests regarding your data.",
              indentLevel: 2
            }
          ]
        }
      ]
    },

    // Section 8: Contact & More Information
    {
      title: "8. Contact & More Information",
      items: [
        {
          content:
            "If you have any questions about this Privacy Policy or your rights as a research participant, please reach out to us at:",
          indentLevel: 0
        },
        {
          content: "Email: support @thebrainhealthproject.org",
          indentLevel: 0
        },
        {
          content:
            "Additionally, please refer to the study's Informed Consent Form for detailed information about your rights, potential risks, and how your data is used in the context of the research study. If you have any concerns that are not addressed by the research team, you may also contact the Institutional Review Board (IRB) overseeing this study as noted in your consent form.",
          indentLevel: 0
        }
      ]
    }
  ],
  footer: {
    effectiveDate: "Effective Date: March 28, 2025",
    lastUpdated: "Last Updated: March 28, 2025",
    disclaimer:
      "This Privacy Policy is intended to be consistent with the IRB-approved Informed Consent Form for the BrainHealth Platform. In the event of any discrepancy, the Informed Consent Form language takes precedence."
  }
};
